import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Hero from 'components/Hero';
import { Content, Title, Btn, ButtonGroup } from 'components/Layout';
import HeroBgImage from 'assets/img/neurodegenerative.png';

const stats = [
  { value: '10k+', label: 'Training compounds' },
  { value: '270-D', label: 'Molecular fingerprint' },
  { value: 'SVM', label: 'Classifier architecture' },
  { value: '0.95+', label: 'Independent test ROC-AUC' },
];

const endpoints = [
  { name: 'BBB', weight: '30%', direction: 'Higher' },
  { name: 'P-gp substrate', weight: '15%', direction: 'Lower' },
  { name: 'P-gp inhibition', weight: '5%', direction: 'Lower' },
  { name: 'PAMPA', weight: '15%', direction: 'Higher' },
  { name: 'Caco-2', weight: '15%', direction: 'Higher' },
  { name: 'PSA', weight: '10%', direction: 'Lower' },
  { name: 'logP', weight: '10%', direction: 'Near ~3' },
];

const cases = [
  {
    disease: "Alzheimer's Disease",
    title: 'Neuroinflammation Inhibitors',
    result: 'IC50 improved from 2.2 µM to 0.3–0.4 µM — a 5–7 fold potency gain',
    metrics: ['BBB score > 0.75', 'Reduced neuroinflammation in 5xFAD mice'],
  },
  {
    disease: "Parkinson's Disease",
    title: 'ASN-401 (α-Synuclein)',
    result: 'AI-designed compound with strong BBB permeability and neuroprotection',
    metrics: ['BBB score 0.87', 'Brain/plasma AUC 0.78', 'r = 0.83 vs. in vivo brain exposure'],
  },
  {
    disease: "Alzheimer's Disease",
    title: 'TAU-073 (Tau aggregation)',
    result: 'Potent inhibition of tau aggregation with excellent brain target engagement',
    metrics: ['BBB score 0.89', 'Brain/plasma AUC 0.86'],
  },
];

const workflowSteps = ['Input', 'Predict', 'Integrate', 'Rank', 'Learn'];

const BBBPredictionPage = () => {
  const navigate = useNavigate();
  const title = 'BBB Prediction';
  const subtitle = 'AI-driven blood-brain barrier permeability prediction for CNS drug discovery';

  return (
    <BBBPrediction>
      <Hero title={title} subtitle={subtitle} bgImage={HeroBgImage} />

      <Content>
        <p>Only about 2–5% of small-molecule drugs — and even fewer biologics — can effectively cross the blood-brain barrier (BBB) with conventional drug-like properties. Promising in vitro compounds often fail in the clinic due to inadequate BBB penetration, rapid clearance, or unfavorable metabolism.</p>
        <p>Genion's BBB prediction platform assesses brain penetration early in the discovery workflow, so unfavorable molecules can be deprioritized before resource-intensive synthesis and experimental characterization — maximizing CNS exposure and efficacy potential while minimizing risk.</p>
      </Content>

      <SectionTitle>Model performance</SectionTitle>
      <StatGrid>
        {stats.map((stat) => (
          <StatTile key={stat.label}>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </StatTile>
        ))}
      </StatGrid>
      <StatNote>Independent test set of 1,554 compounds: 890 BBB-positive and 492 BBB-negative correctly classified.</StatNote>

      <SectionTitle>Direction-aware CNS desirability scoring</SectionTitle>
      <Content as="div">
        <p>A multi-parameter score converts heterogeneous BBB-related endpoints into an interpretable prioritization layer. Higher is not always better — for example, lower PSA favors CNS penetration, and logP is treated as an optimum-range property rather than maximized.</p>
        <TableWrapper>
          <ScoreTable>
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Weight</th>
                <th>Desired direction</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((e) => (
                <tr key={e.name}>
                  <td>{e.name}</td>
                  <td>{e.weight}</td>
                  <td>{e.direction}</td>
                </tr>
              ))}
            </tbody>
          </ScoreTable>
        </TableWrapper>
        <Caveat>The score is a relative computational prioritization metric for a given compound set — not a validated clinical exposure score.</Caveat>
      </Content>

      <SectionTitle>Case study</SectionTitle>
      <Content>
        <p>Across a 200+ compound scaffold series, Genion’s platform evaluated each candidate across seven BBB-related endpoints (BBB, P-gp substrate, P-gp inhibition, PAMPA, Caco-2, PSA, and logP) to generate a scaffold CNS-desirability ranking. BBB endpoint values ranged from 0.07 to 0.49, providing broad, actionable separation across the series. Notably, multiple newly designed compounds achieved top-tier BBB signals (~0.49), while modified analogs consistently outranked their predecessors.</p>
      </Content>

      <SectionTitle>Validated in three disease areas</SectionTitle>
      <CaseGrid>
        {cases.map((c) => (
          <CaseCard key={c.title}>
            <CaseDisease>{c.disease}</CaseDisease>
            <CaseTitle>{c.title}</CaseTitle>
            <CaseResult>{c.result}</CaseResult>
            <CaseMetrics>
              {c.metrics.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </CaseMetrics>
          </CaseCard>
        ))}
      </CaseGrid>

      <SectionTitle>How it fits into your workflow</SectionTitle>
      <WorkflowRow>
        {workflowSteps.map((step, i) => (
          <React.Fragment key={step}>
            <WorkflowStep>{step}</WorkflowStep>
            {i < workflowSteps.length - 1 && <WorkflowArrow>→</WorkflowArrow>}
          </React.Fragment>
        ))}
      </WorkflowRow>

      <CtaWrapper>
        <ButtonGroup>
          <Btn type="primary" size="large" onClick={() => navigate('/contact')}>
            Contact Us
          </Btn>
        </ButtonGroup>
      </CtaWrapper>
    </BBBPrediction>
  );
};

const BBBPrediction = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const SectionTitle = styled(Title)`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-top: ${({ theme }) => theme.spacing(8)};
  color: ${({ theme }) => theme.colors.text};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
  max-width: 900px;
  margin: ${({ theme }) => theme.spacing(4)} auto 0;

  @media (max-width: ${({ theme }) => theme.tabletWidth}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatTile = styled.div`
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing(3)};
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba(49, 110, 177, 0.95) 0%,
    rgba(78, 158, 163, 0.95) 100%
  );
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: 700;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  margin-top: ${({ theme }) => theme.spacing(1)};
  opacity: 0.9;
`;

const StatNote = styled.p`
  max-width: 900px;
  margin: ${({ theme }) => theme.spacing(2)} auto 0;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.text};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const ScoreTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: ${({ theme }) => theme.fontSizes.s};

  th, td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  }

  th {
    font-weight: 700;
  }
`;

const Caveat = styled.p`
  font-style: italic;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.85;
`;

const CaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(3)};
  max-width: 1100px;
  margin: ${({ theme }) => theme.spacing(4)} auto 0;
  padding: 0 ${({ theme }) => theme.spacing(4)};

  @media (max-width: ${({ theme }) => theme.tabletWidth}) {
    grid-template-columns: 1fr;
  }
`;

const CaseCard = styled.div`
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 8px 24px #00000014;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const CaseDisease = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xxs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.primary};
`;

const CaseTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
`;

const CaseResult = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.s};
  color: ${({ theme }) => theme.colors.text};
`;

const CaseMetrics = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(2)};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.text};
`;

const WorkflowRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: ${({ theme }) => theme.spacing(4)} auto 0;
`;

const WorkflowStep = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.s};
`;

const WorkflowArrow = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

const CtaWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: ${({ theme }) => theme.spacing(6)} auto ${({ theme }) => theme.spacing(4)};
`;

export default BBBPredictionPage;
